import React from "react";
import { formatPatientInitials } from "../formatUtils";
import { isLoading, Loading } from "../Loading";
import * as messages from "../messages";
import {
  CaregiverPatientDocument,
  PatientDocumentScanCreate,
  VisitInstanceDetails,
} from "../messages";
import { AgencyId, PatientId, VisitInstanceId } from "../messages/ObjectIds";
import {
  NursingQuestionVBPAnswer,
  NursingQuestionVBPReportSubmit,
  NursingQuestionVBPWithAnswer,
} from "../messages/VBP";
import { STRINGS } from "../strings";
import { AgencyMember } from "../system/AppState/AppAgenciesState";
import { AppCaregiver } from "../system/AppState/AppCaregiverState";
import { translate } from "../translate";
import { getLocationDisplayLocation } from "../utils/addressComponentUtils";
import { doNothing } from "../utils/doNothing";
import { AgencyDetailsCard } from "./AgencyDetailsCard";
import BadgesGroup from "./BadgesGroup";
import { If } from "./Generic/If";
import ListCard from "./ListCard";
import { MapWidget } from "./MapWidget";
import NotesCard from "./Patient/components/NotesCard";
import ScaffoldTitle from "./Scaffold/components/ScaffoldTitle";
import Scaffold from "./Scaffold/Scaffold";
import StartNowBottomSheetFooter from "./StartNowBottomSheetFooter";
import { InitialPlanOfCare } from "./useVisitInstancePlanOfCare";
import VBPQuestionsSection from "./VBPQuestion/components/VBPQuestionsSection";
import VisitChatIcon from "./VisitChatIcon";
import VisitInstanceDetailsView from "./VisitInstanceDetailsView";
import VisitInstanceDocuments from "./VisitInstanceDocuments";
import { VisitInstanceDocumentsAndPlanOfCareState } from "./VisitInstanceDocumentsAndPlanOfCareActivity";
import VisitStaffingPreferencesBadges from "./VisitStaffingPreferencesBadges";
import { AgenciesBlock } from "./AgenciesList/AgenciesListUtils";
import { List } from "../utils/List";
import { PatientDirectivesCard } from "./PatientDirectivesCard";

interface Props {
  caregiver: AppCaregiver;
  coordinator: AgencyMember;
  visitInstance: VisitInstanceDetails;
  numNewChatMessages: number;
  clockStatus: messages.ClockedInStatus;
  vbpItemWithAnswers: NursingQuestionVBPWithAnswer[] | Loading;
  canClockIn: boolean;
  canClockOut: boolean;
  canSubmitTimesheet: boolean;
  isPopperOpen: boolean;
  documentsState: VisitInstanceDocumentsAndPlanOfCareState;
  activeAgencies: List<AgenciesBlock>;
  onChangeVBPQuestion: (vbpQuestion: NursingQuestionVBPWithAnswer) => void;
  onSubmitVBPQuestions: (vbpReport: NursingQuestionVBPReportSubmit) => Promise<void>;
  onAnswerVBPQuestion: (vbpAnswer: NursingQuestionVBPAnswer) => Promise<void>;
  onClickChat: (patientId: PatientId, visitInstanceId: VisitInstanceId) => void;
  onClickCall: (agencyId: AgencyId, phoneNumber: string | null) => void;
  onClickClockIn: () => void;
  onClickClockOut: () => void;
  onClickSubmitTimesheet: () => void;
  onClickOpenPatient: (patientId: PatientId) => void;
  onClickCallPatient: () => void;
  onClickDirections: () => void;
  onClickDeleteFreeScan: (document: CaregiverPatientDocument.FreeScan) => void;
  onUploadDocument: (data: PatientDocumentScanCreate) => void;
  onClickDocument: (document: CaregiverPatientDocument) => void;
  onClickPlanOfCare: (planOfCare: InitialPlanOfCare) => void;
  onClickGoBack: () => void;
  onClickOpenBottomSheet: (visitInstance: VisitInstanceDetails) => void;
}

const VisitInstanceScreen = (props: Props) => {
  const isMedflytCaregiver = props.caregiver.isCaregiverOfMedFlyt;
  const activeAgenciesCount =
    props.activeAgencies
      .filter((a) => a.status === "ACTIVE")
      .first()
      ?.agencies.length() ?? 0;
  const isMedfytCaregiverWithOtherActiveAgencies = isMedflytCaregiver && activeAgenciesCount > 1;

  const handleClickChat = () => {
    props.onClickChat(props.visitInstance.publicVisitDetails.patientId, props.visitInstance.id);
  };

  const handleClickCall = () => {
    props.onClickCall(
      props.visitInstance.publicVisitDetails.agency.id,
      props.visitInstance.publicVisitDetails.agency.officePhone
    );
  };

  const title = (
    <ScaffoldTitle
      label={getLocationDisplayLocation(props.visitInstance.publicVisitDetails.location)}
    />
  );

  const chatAction = (
    <VisitChatIcon
      badge={props.numNewChatMessages}
      isPopperOpen={props.isPopperOpen}
      onClick={() =>
        props.onClickChat(props.visitInstance.publicVisitDetails.patientId, props.visitInstance.id)
      }
    />
  );

  const footer = (
    <StartNowBottomSheetFooter
      canSubmitTimeSheet={props.canSubmitTimesheet}
      onSubmitTimeSheet={props.onClickSubmitTimesheet}
      onClickOpenBottomSheet={props.onClickOpenBottomSheet}
      visitDetails={props.visitInstance}
    />
  );

  return (
    <Scaffold
      title={title}
      endActions={chatAction}
      footer={footer}
      className="VisitInstanceScreen"
      overlayHeader={true}
      onClickGoBack={props.onClickGoBack}
    >
      <MapWidget
        origin={props.caregiver.addressGeoLocation}
        destination={props.visitInstance.publicVisitDetails.location.location}
        patientInitials={formatPatientInitials(
          props.visitInstance.publicVisitDetails.patientInitials
        )}
        onOpenMapClick={props.onClickDirections}
      />

      <BadgesGroup labels={props.visitInstance.publicVisitDetails.visitLabels} />

      <If condition={props.visitInstance.publicVisitDetails.agency.isMedflytAgency}>
        <VisitStaffingPreferencesBadges
          staffingPreferences={props.visitInstance.publicVisitDetails.staffingPreferences2}
        />
      </If>

      <VisitInstanceDetailsView
        patientName={props.visitInstance.confidentialVisitDetails.patientName}
        patientGender={props.visitInstance.publicVisitDetails.patientGender}
        patientAge={props.visitInstance.publicVisitDetails.patientAge}
        patientLanguages={props.visitInstance.publicVisitDetails.patientLanguages}
        patientId={props.visitInstance.publicVisitDetails.patientId}
        address={props.visitInstance.confidentialVisitDetails.patientAddress}
        address2={props.visitInstance.confidentialVisitDetails.patientAddress2}
        addressInstructions={
          props.visitInstance.confidentialVisitDetails.patientAddressInstructions
        }
        startDateTime={props.visitInstance.startTimeLocal}
        endDateTime={props.visitInstance.endTimeLocal}
        onClickOpenPatient={props.onClickOpenPatient}
        onClickCallPatient={props.onClickCallPatient}
        onClickDirections={props.onClickDirections}
      />

      {props.visitInstance.publicVisitDetails.patientAdvancedDirectives !== null && (
        <PatientDirectivesCard
          advancedDirectives={props.visitInstance.publicVisitDetails.patientAdvancedDirectives}
        />
      )}

      <If condition={props.visitInstance.publicVisitDetails.agency.isMedflytAgency}>
        <ListCard
          title={translate(STRINGS.PATIENT_REQUESTS)}
          items={props.visitInstance.publicVisitDetails.patientRequests}
        />

        <ListCard
          title={translate(STRINGS.MAIN_TASKS)}
          items={props.visitInstance.publicVisitDetails.patientTasks}
        />
      </If>

      <VisitInstanceDocuments
        data={props.documentsState}
        onUploadFreeScan={props.onUploadDocument}
        onDeleteFreeScan={props.onClickDeleteFreeScan}
        onClickDocument={props.onClickDocument}
        onClickPlanOfCare={props.onClickPlanOfCare}
      />

      {props.visitInstance.publicVisitDetails.patientAllergies.length > 0 ? (
        <ListCard
          title={translate(STRINGS.ALLERGIES)}
          items={props.visitInstance.publicVisitDetails.patientAllergies.map(
            (allergy) => allergy.name
          )}
        />
      ) : (
        <NotesCard title={translate(STRINGS.ALLERGIES)} notes={null} />
      )}

      <NotesCard
        title={translate(STRINGS.MEDICAL_INFORMATION)}
        notes={props.visitInstance.confidentialVisitDetails.caseDetails}
      />

      <NotesCard
        title={translate(STRINGS.GENERAL_NOTES)}
        notes={props.visitInstance.publicVisitDetails.generalNotes}
      />

      {!isLoading(props.vbpItemWithAnswers) && !props.caregiver.isCaregiverOfMedFlyt ? (
        <VBPQuestionsSection
          showAnsweredIndication={false}
          showTitle={true}
          nursingQuestionsVBPWithAnswers={props.vbpItemWithAnswers}
          onVBPQuestionChange={props.onChangeVBPQuestion}
          onSubmitVBPQuestions={props.onSubmitVBPQuestions}
          onVBPQuestionAnswer={props.onAnswerVBPQuestion}
          onVBPQuestionNotesClick={doNothing}
        />
      ) : null}
      {(isMedfytCaregiverWithOtherActiveAgencies || !isMedflytCaregiver) && (
        <AgencyDetailsCard
          agency={props.visitInstance.publicVisitDetails.agency}
          coordinatorInCharge={props.coordinator}
          onClickChat={handleClickChat}
          onClickCall={handleClickCall}
          showCallButton={!props.caregiver.isCaregiverOfMedFlyt}
        />
      )}
    </Scaffold>
  );
};

export default VisitInstanceScreen;
